/**
 * Export presets (M4S-5777 X105): how a delivered workbook is laid out for a client or a study
 * type, so nobody cleans one up by hand. A preset is data: the house standard lives here in code
 * (`HOUSE_PRESET`, the workbook as MFour delivers it), and the others are rows of
 * `atlas.pdlc_crosstab_export_preset` (migrations/studio/pdlc_crosstab_export_preset.sql), edited on
 * /tools/crosstab-export-presets and each assigned to an account, a study type, both or neither.
 * `BUILT_IN_PRESETS` holds the seeded ones too (ABC episodic, OOH), offered only where the table is
 * missing so an environment without the migration still has them.
 *
 * A preset decides formatting and names only, never a figure, a label, a row order or which sheet
 * a reader takes (Appendix A rule 15): the Excel writer reads every width, frozen pane, sheet name,
 * sheet order, the table of contents and the per-table notes from `ExportLayout`, and a guard
 * (`export-presets.guard.test.ts`) fails on a width or a frozen pane written into the writer.
 *
 * The preset a crosstab exports with is remembered on the crosstab (`ai_layout.state.exportPreset`,
 * a record setting): `resolveExportPreset` takes that one when it still exists, else the most
 * specific assignment (account and study type, then account, then study type), else the house's.
 *
 * Pure: no database, no React. Spec: docs/pdlc/crosstabs-deliverables.md §4.2.
 */

import { STUDY_TYPES, type StudyTypeKey } from './study-type';

/** The id the house standard always has. It is never a row: the house is code. */
export const HOUSE_PRESET_ID = 'house';

/** A preset id: lower-case letters, digits and dashes, up to 64 (the table checks the same). */
export const EXPORT_PRESET_ID = /^[a-z0-9][a-z0-9-]{0,63}$/;

/**
 * The Excel layout a preset delivers: every question on one sheet, one question per sheet, or
 * Total plus one sheet per filter-tab answer (X106; a crosstab without filter tabs gets the
 * combined sheet).
 */
export const EXCEL_LAYOUTS = ['combined', 'tabs', 'by-answer'] as const;
export type ExcelLayout = (typeof EXCEL_LAYOUTS)[number];

export const EXCEL_LAYOUT_LABELS: Record<ExcelLayout, string> = {
    combined: 'Every question on one sheet',
    tabs: 'One question per sheet',
    'by-answer': 'Total plus one sheet per filter answer',
};

/** Where the cover ("Summary": what the file is and what produced it) sits. */
export type CoverPosition = 'first' | 'last';

/** The formatting a preset decides. Every field is required in a stored preset. */
export interface ExportPresetConfig {
    excelLayout: ExcelLayout;
    /** Column A (question and answer labels), in Excel character widths. */
    labelColumnWidth: number;
    /** A banner column's width: fitted to its label between `min` and `max`; equal is fixed. */
    bannerColumnWidth: { min: number; max: number };
    /** Freeze the header rows and column A. */
    freezePanes: boolean;
    coverSheet: CoverPosition;
    /** The combined data sheet's name; null names it after the crosstab. */
    dataSheetName: string | null;
    /** A linked table of contents (X104). */
    contents: boolean;
    /** The notes under every table: comparison groups, filter, weighting, low base (X104). */
    footnotes: boolean;
    /** Initials and dates out of the crosstab name in file and sheet names (OOH). */
    stripInitialsAndDates: boolean;
    /** The export date in the file name. */
    fileDate: boolean;
    /** The open-end verbatims sheet starts switched on (X112). Never respondent ids. */
    verbatims: boolean;
}

export interface ExportPreset {
    id: string;
    name: string;
    description: string;
    /** The account (Salesforce account id) it is assigned to, or null for any. */
    orgId: string | null;
    /** The study type it is assigned to, or null for any. */
    studyType: StudyTypeKey | null;
    config: ExportPresetConfig;
    active: boolean;
    version: number;
}

/**
 * The house standard: the workbook as M4S-5666 laid it out (column A 45 wide, banner columns 14 to
 * 35, frozen header), plus the linked table of contents and the notes under every table the Crosstab
 * QA Checklist asks for. `export-presets.test.ts` pins the widths and panes to the writer's old ones.
 */
export const HOUSE_CONFIG: Readonly<ExportPresetConfig> = Object.freeze({
    excelLayout: 'combined',
    labelColumnWidth: 45,
    bannerColumnWidth: Object.freeze({ min: 14, max: 35 }),
    freezePanes: true,
    coverSheet: 'first',
    dataSheetName: null,
    contents: true,
    footnotes: true,
    stripInitialsAndDates: false,
    fileDate: true,
    verbatims: false,
} as ExportPresetConfig);

export const HOUSE_PRESET: Readonly<ExportPreset> = Object.freeze({
    id: HOUSE_PRESET_ID,
    name: 'MFour house standard',
    description:
        'Cover, linked table of contents, every question on one sheet with notes under each table.',
    orgId: null,
    studyType: null,
    config: HOUSE_CONFIG,
    active: true,
    version: 1,
});

/**
 * The seeded presets, as the migration inserts them. Offered from code only where the table is
 * missing; where it exists the rows are the truth (an admin may have edited them).
 *
 * - ABC episodic: the ABC workflow page's hand clean-up (Confluence 3675226113): first sheet named
 *   "Crosstab", column A 40 and banner columns 20 wide, header frozen, and the OE sheet. The cover
 *   moves last so the data is the first sheet; it stays in the file (provenance, P2).
 * - OOH: the house layout, with initials and dates taken out of file and sheet names. The OOH rule
 *   page was not found; this is the rule as described (spec §8, open question).
 */
export const BUILT_IN_PRESETS: readonly ExportPreset[] = Object.freeze([
    {
        id: 'abc-episodic',
        name: 'ABC episodic',
        description:
            'First sheet "Crosstab", column A 40 and banner columns 20 wide, frozen header, OE sheet.',
        orgId: null,
        studyType: null,
        config: {
            ...HOUSE_CONFIG,
            labelColumnWidth: 40,
            bannerColumnWidth: { min: 20, max: 20 },
            coverSheet: 'last',
            dataSheetName: 'Crosstab',
            verbatims: true,
        },
        active: true,
        version: 1,
    },
    {
        id: 'ooh',
        name: 'OOH',
        description: 'House layout; initials and dates taken out of the file and sheet names.',
        orgId: null,
        studyType: 'iooh',
        config: { ...HOUSE_CONFIG, stripInitialsAndDates: true },
        active: true,
        version: 1,
    },
]);

// ── Validation (the one rule the route, the store and the admin page share) ─────────────────────

const WIDTH_MIN = 5;
const WIDTH_MAX = 120;
const SHEET_NAME_MAX = 31;
const SHEET_NAME_BAD = /[[\]:*?/\\]|^'|'$/;

const isObject = (v: unknown): v is Record<string, unknown> =>
    !!v && typeof v === 'object' && !Array.isArray(v);

function width(v: unknown, field: string, errors: string[]): number {
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n) || n < WIDTH_MIN || n > WIDTH_MAX) {
        errors.push(`${field} must be a width from ${WIDTH_MIN} to ${WIDTH_MAX}.`);
        return WIDTH_MIN;
    }
    return Math.round(n * 10) / 10;
}

function bool(v: unknown, field: string, errors: string[]): boolean {
    if (typeof v !== 'boolean') errors.push(`${field} must be true or false.`);
    return v === true;
}

/** A config, checked field by field; every reason it is wrong, or the config in canonical form. */
export function validateExportPresetConfig(
    raw: unknown,
): { ok: true; config: ExportPresetConfig } | { ok: false; errors: string[] } {
    if (!isObject(raw)) return { ok: false, errors: ['The preset needs its settings.'] };
    const errors: string[] = [];
    const excelLayout = (EXCEL_LAYOUTS as readonly unknown[]).includes(raw.excelLayout)
        ? (raw.excelLayout as ExcelLayout)
        : (errors.push(`Excel layout must be one of ${EXCEL_LAYOUTS.join(', ')}.`), 'combined');
    const labelColumnWidth = width(raw.labelColumnWidth, 'Column A width', errors);
    const bcw = isObject(raw.bannerColumnWidth) ? raw.bannerColumnWidth : {};
    const min = width(bcw.min, 'Banner column width (smallest)', errors);
    const max = width(bcw.max, 'Banner column width (largest)', errors);
    if (min > max) errors.push('The smallest banner column width is larger than the largest.');
    const coverSheet =
        raw.coverSheet === 'first' || raw.coverSheet === 'last'
            ? raw.coverSheet
            : (errors.push('The cover sheet goes first or last.'), 'first' as const);
    let dataSheetName: string | null = null;
    if (raw.dataSheetName != null && raw.dataSheetName !== '') {
        const name = typeof raw.dataSheetName === 'string' ? raw.dataSheetName.trim() : '';
        if (!name || name.length > SHEET_NAME_MAX || SHEET_NAME_BAD.test(name)) {
            errors.push(
                `The data sheet name must be 1 to ${SHEET_NAME_MAX} characters without [ ] : * ? / \\ or a leading or trailing apostrophe.`,
            );
        } else dataSheetName = name;
    }
    const config: ExportPresetConfig = {
        excelLayout,
        labelColumnWidth,
        bannerColumnWidth: { min, max },
        freezePanes: bool(raw.freezePanes, 'Freeze panes', errors),
        coverSheet,
        dataSheetName,
        contents: bool(raw.contents, 'Table of contents', errors),
        footnotes: bool(raw.footnotes, 'Notes under every table', errors),
        stripInitialsAndDates: bool(raw.stripInitialsAndDates, 'Strip initials and dates', errors),
        fileDate: bool(raw.fileDate, 'Date in the file name', errors),
        verbatims: bool(raw.verbatims, 'Verbatims sheet', errors),
    };
    return errors.length ? { ok: false, errors } : { ok: true, config };
}

const STUDY_TYPE_KEYS = new Set<string>(STUDY_TYPES.map((t) => t.key));

/** A whole preset, checked; every reason it is wrong, or the preset in canonical form. */
export function validateExportPreset(
    raw: unknown,
): { ok: true; preset: ExportPreset } | { ok: false; errors: string[] } {
    if (!isObject(raw)) return { ok: false, errors: ['The preset must be an object.'] };
    const errors: string[] = [];
    const id = typeof raw.id === 'string' ? raw.id : '';
    if (!EXPORT_PRESET_ID.test(id)) {
        errors.push('The id must be lower-case letters, digits and dashes (up to 64).');
    } else if (id === HOUSE_PRESET_ID) {
        errors.push('"house" is the house standard, kept in code; pick another id.');
    }
    const name = typeof raw.name === 'string' ? raw.name.trim() : '';
    if (!name || name.length > 120) errors.push('The name must be 1 to 120 characters.');
    const description = typeof raw.description === 'string' ? raw.description.trim() : '';
    if (description.length > 500) errors.push('The description must be at most 500 characters.');
    const orgId =
        raw.orgId == null || raw.orgId === ''
            ? null
            : typeof raw.orgId === 'string'
              ? raw.orgId.trim()
              : '';
    if (orgId !== null && !/^[A-Za-z0-9]{15,18}$/.test(orgId)) {
        errors.push('The account must be a Salesforce account id (15 or 18 characters), or empty.');
    }
    const studyType =
        raw.studyType == null || raw.studyType === ''
            ? null
            : STUDY_TYPE_KEYS.has(String(raw.studyType))
              ? (raw.studyType as StudyTypeKey)
              : (errors.push('The study type is not one AI Crosstabs knows.'), null);
    const version = Number(raw.version);
    if (!Number.isInteger(version) || version < 1) errors.push('The version must be 1 or more.');
    const config = validateExportPresetConfig(raw.config);
    if (!config.ok) errors.push(...config.errors);
    if (errors.length || !config.ok) return { ok: false, errors };
    return {
        ok: true,
        preset: {
            id,
            name,
            description,
            orgId,
            studyType,
            config: config.config,
            active: raw.active !== false,
            version,
        },
    };
}

/** A stored preset id, or null for anything else (the record's `exportPreset`). */
export function parseExportPresetId(raw: unknown): string | null {
    return typeof raw === 'string' && EXPORT_PRESET_ID.test(raw) ? raw : null;
}

// ── Choosing ──────────────────────────────────────────────────────────────────────────────────

/** The presets a crosstab may pick: the house first, then every active one, by name. */
export function offeredPresets(rows: readonly ExportPreset[]): ExportPreset[] {
    const active = rows
        .filter((p) => p.active && p.id !== HOUSE_PRESET_ID)
        .sort((a, b) => a.name.localeCompare(b.name, 'en') || a.id.localeCompare(b.id, 'en'));
    return [HOUSE_PRESET, ...active];
}

/** How closely a preset is assigned to a crosstab: 3 account and type, 2 account, 1 type, 0 none. */
function specificity(
    p: ExportPreset,
    ctx: { orgId?: string | null; studyType?: string | null },
): number {
    const orgMatch = p.orgId !== null && !!ctx.orgId && p.orgId === ctx.orgId;
    const typeMatch = p.studyType !== null && !!ctx.studyType && p.studyType === ctx.studyType;
    if (p.orgId !== null && !orgMatch) return -1;
    if (p.studyType !== null && !typeMatch) return -1;
    return (orgMatch ? 2 : 0) + (typeMatch ? 1 : 0);
}

/**
 * The preset a crosstab exports with: the one it remembers, when it is still offered; else the
 * most specific assignment to its account and study type (ties by name); else the house's.
 */
export function resolveExportPreset(
    offered: readonly ExportPreset[],
    ctx: { remembered?: string | null; orgId?: string | null; studyType?: string | null },
): ExportPreset {
    const remembered = ctx.remembered
        ? offered.find((p) => p.id === ctx.remembered && p.active)
        : undefined;
    if (remembered) return remembered;
    let best: ExportPreset | null = null;
    let bestScore = 0;
    for (const p of offered) {
        if (!p.active || p.id === HOUSE_PRESET_ID) continue;
        const score = specificity(p, ctx);
        if (score > bestScore) {
            best = p;
            bestScore = score;
        }
    }
    return best ?? HOUSE_PRESET;
}

/** The preset's Excel layout for a crosstab: "by answer" only where it has filter tabs. */
export function excelLayoutFor(preset: ExportPreset, hasFilterTabs: boolean): ExcelLayout {
    return preset.config.excelLayout === 'by-answer' && !hasFilterTabs
        ? 'combined'
        : preset.config.excelLayout;
}

// ── What the Excel writer reads ───────────────────────────────────────────────────────────────

/**
 * Every width, pane and name the Excel writer uses, with the preset applied. A writer handed no
 * layout uses `PLAIN_LAYOUT`: the workbook exactly as it was written before presets, with no table
 * of contents and no per-table notes, which is what the readers' snapshots were recorded from.
 */
export interface ExportLayout {
    labelColumnWidth: number;
    bannerColumnWidth: { min: number; max: number };
    freezePanes: boolean;
    coverSheet: CoverPosition;
    dataSheetName: string | null;
    contents: boolean;
    footnotes: boolean;
    stripInitialsAndDates: boolean;
    /** The cover's two columns. */
    coverColumnWidths: readonly [number, number];
    /** The cover's second column when it lists each sheet's filter (X106's workbook). */
    sheetListWidth: number;
    /** The table of contents' columns: sheet, question, link. */
    contentsColumnWidths: readonly [number, number, number];
    /** The verbatims sheet's columns: question, respondent, survey, verbatim, codes. */
    verbatimColumnWidths: readonly [number, number, number, number, number];
}

const SHARED_WIDTHS = {
    coverColumnWidths: [28, 50] as const,
    sheetListWidth: 70,
    contentsColumnWidths: [32, 80, 14] as const,
    verbatimColumnWidths: [14, 12, 18, 90, 40] as const,
};

export const PLAIN_LAYOUT: Readonly<ExportLayout> = Object.freeze({
    labelColumnWidth: HOUSE_CONFIG.labelColumnWidth,
    bannerColumnWidth: HOUSE_CONFIG.bannerColumnWidth,
    freezePanes: true,
    coverSheet: 'first',
    dataSheetName: null,
    contents: false,
    footnotes: false,
    stripInitialsAndDates: false,
    ...SHARED_WIDTHS,
});

/** The layout the Excel writer reads for a preset. */
export function exportLayoutOf(preset: Pick<ExportPreset, 'config'>): ExportLayout {
    const c = preset.config;
    return {
        labelColumnWidth: c.labelColumnWidth,
        bannerColumnWidth: { ...c.bannerColumnWidth },
        freezePanes: c.freezePanes,
        coverSheet: c.coverSheet,
        dataSheetName: c.dataSheetName,
        contents: c.contents,
        footnotes: c.footnotes,
        stripInitialsAndDates: c.stripInitialsAndDates,
        ...SHARED_WIDTHS,
    };
}

/** A banner column's width under a layout: its label's length plus four, within the bounds. */
export function bannerColumnWidthFor(layout: ExportLayout, label: string): number {
    const { min, max } = layout.bannerColumnWidth;
    return Math.min(max, Math.max(min, label.length + 4));
}
